/**
 * Plan links: `/pricing`, `/pricing/{plan}` and `/pricing/{plan}/{option}`
 * (e.g. `/pricing/growth/anual`). The plan part is the plan's `slug`, the
 * option part the product's `optionKey`, both from the API.
 */

import { ROUTES } from '@/lib/routes'

export const PRICING_PATH = '/pricing'

/** True for `/pricing` and anything under it. */
export const isPricingPath = (path: string): boolean => path === PRICING_PATH || path.startsWith(`${PRICING_PATH}/`)

export interface PricingSelection {
  plan: string | null
  option: string | null
}

/** The `{plan}/{option}` segments after a prefix, decoded; the query and hash are not part of them. */
const selectionAfter = (fullPath: string, prefix: string): PricingSelection => {
  const path = fullPath.split(/[?#]/)[0]
  if (path !== prefix && !path.startsWith(`${prefix}/`)) return { plan: null, option: null }
  const [plan, option] = path
    .slice(prefix.length)
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment)
      } catch {
        return segment
      }
    })
  return { plan: plan || null, option: option || null }
}

/* The route may carry a query (`?addOns=`, `?back=`, `?plans=`): it is not part of the option. */
export const parsePricingPath = (fullPath: string): PricingSelection => selectionAfter(fullPath, PRICING_PATH)

export const pricingPath = (plan?: string | null, option?: string | null): string => {
  if (!plan) return PRICING_PATH
  const base = `${PRICING_PATH}/${encodeURIComponent(plan)}`
  return option ? `${base}/${encodeURIComponent(option)}` : base
}

/**
 * A plan link from the offer of a padlocked space: `?addOns=` ticks those
 * add-ons on the plan's page (when the plan sells them) and `?back=` is where
 * the confirmation returns once paid (the space, now open).
 */
export const pricingOfferPath = (plan: string, option: string | null | undefined, extra: { addOnIds?: string[]; back?: string | null }): string => {
  const params = new URLSearchParams()
  if (extra.addOnIds?.length) params.set('addOns', extra.addOnIds.join(','))
  if (extra.back) params.set('back', extra.back)
  const query = params.toString()
  return query ? `${pricingPath(plan, option)}?${query}` : pricingPath(plan, option)
}

/**
 * F-195: the plan change, with the checkout's layout, for someone who already
 * has a plan: `/_/subscriptions/change-plan/{plan}/{option}`. The same
 * `?addOns=` and `?back=` as `pricingOfferPath`; `company` and `subscription`
 * point at a company's subscription (F-93 1B, its admin changes it). With no
 * plan, the page opens on the plan list.
 */
export const CHANGE_PLAN_PATH: string = ROUTES.changePlan

export const isChangePlanPath = (path: string): boolean => {
  const route = path.split(/[?#]/)[0]
  return route === CHANGE_PLAN_PATH || route.startsWith(`${CHANGE_PLAN_PATH}/`)
}

export const parseChangePlanPath = (fullPath: string): PricingSelection => selectionAfter(fullPath, CHANGE_PLAN_PATH)

export interface ChangePlanLinkExtra {
  addOnIds?: string[]
  back?: string | null
  companyId?: string | null
  subscriptionId?: string | null
}

export const changePlanPath = (plan?: string | null, option?: string | null, extra: ChangePlanLinkExtra = {}): string => {
  let path = CHANGE_PLAN_PATH
  if (plan) {
    path += `/${encodeURIComponent(plan)}`
    if (option) path += `/${encodeURIComponent(option)}`
  }
  const params = new URLSearchParams()
  if (extra.addOnIds?.length) params.set('addOns', extra.addOnIds.join(','))
  if (extra.back) params.set('back', extra.back)
  if (extra.companyId) params.set('company', extra.companyId)
  if (extra.subscriptionId) params.set('subscription', extra.subscriptionId)
  const query = params.toString()
  return query ? `${path}?${query}` : path
}
